import type { SamplingTimelineJob, SamplingTimelineProps } from './sampling-timeline.types.ts';
import { formatDate } from '@progress/kendo-intl';
import styles from './sampling-timeline.module.scss';
import type { CSSProperties } from 'react';

const MS_PER_DAY = 86_400_000;
const SAMPLER_PALETTE = [
  '#28B4C8',
  '#FF6358',
  '#78D237',
  '#FFD246',
  '#AA46BE',
  '#2D73F5',
  '#FF7043',
  '#26A69A',
  '#EC407A',
  '#7E57C2',
  '#D4E157',
  '#FFA726',
  '#26C6DA',
  '#8D6E63',
  '#AB47BC',
  '#9CCC65',
  '#42A5F5',
  '#FFCA28',
  '#5C6BC0',
  '#26A69A',
  '#EF5350',
  '#66BB6A',
  '#FF8A65',
  '#78909C',
  '#80DEEA',
  '#A1887F',
  '#CE93D8',
  '#C5E1A5',
  '#90CAF9',
  '#FFE082',
  '#B39DDB',
  '#B2DFDB',
];

export const getJobStyle = (job: SamplingTimelineJob, dateRange: Date[]) => {
  const rangeStartMs = new Date(dateRange[0]).getTime();
  const jobStartMs = new Date(job.startDate).getTime();
  const jobEndMs = new Date(job.endDate).getTime();

  const startCol = Math.max(1, Math.round((jobStartMs - rangeStartMs) / MS_PER_DAY) + 1);
  const duration = Math.max(1, Math.round((jobEndMs - jobStartMs) / MS_PER_DAY));
  const span = Math.min(duration, Math.max(0, dateRange.length - startCol + 1));

  return {
    gridColumnStart: startCol,
    gridColumnEnd: `span ${span}`,
  };
};

export function SamplingTimeline({ className, samplers, jobs, dateRange, onJobClick }: SamplingTimelineProps) {
  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.header}>
        <div></div>
        {dateRange.map((date) => (
          <div key={date.toISOString()} className={styles.dayHeaderCell}>
            <span className={styles.headerCellSubtitle}>{formatDate(date, 'EEE')}</span>
            <span className={styles.headerCellTitle}>{formatDate(date, 'd MMM yy')}</span>
          </div>
        ))}
      </div>

      <div className={styles.content}>
        {samplers.map((sampler, idx) => {
          const samplerColor = SAMPLER_PALETTE[idx];
          const samplerJobs = jobs.filter((job) => {
            return (
              job.samplerIds.includes(sampler.id) &&
              job.startDate >= dateRange[0] &&
              job.endDate <= dateRange[dateRange.length - 1]
            );
          });

          return (
            <div key={sampler.id} className={styles.samplerRow}>
              <div className={styles.samplerInfo}>
                <div className={styles.avatarIndicator} style={{ backgroundColor: samplerColor }} />
                <div>
                  <div className={styles.samplerName}>{`${sampler.forename} ${sampler.surname}`}</div>
                  <div className={styles.samplerUsername}>{sampler.username}</div>
                </div>
              </div>
              <div className={styles.daysTrack}>
                <div className={styles.gridBackground}>
                  {dateRange.map((date) => (
                    <div key={date.toISOString()} className={styles.gridColumn} />
                  ))}
                </div>
                <div className={styles.jobsContainer}>
                  {samplerJobs.map((job) => {
                    const jobStyle = getJobStyle(job, dateRange);
                    return (
                      <div
                        key={job.id}
                        className={styles.jobCard}
                        style={
                          {
                            ...jobStyle,
                            '--sampler-color': samplerColor,
                          } as CSSProperties
                        }
                        onClick={() => onJobClick?.(job)}
                      >
                        <div className={styles.jobTitle}>{job.clientTitle}</div>
                        <div className={styles.jobSubtitle}>Est. Days: {job.estimatedDays}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
